/*@flow*/
import type { Element } from 'react'; /** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Fragment, useState, useEffect, createRef } from 'react';
import truncate from 'underscore.string/truncate';
import GetProjectsPaginated from './queries/GetProjectsPaginated';
import useContextState from './useContextState';
import useCommands from './useCommands';
import isPath from 'lib/isPath';
import scrollContainerTop from './scrollContainerTop';
import * as I from 'immutable';
import useCachedQuery from 'lib/useCachedQuery';
import useFilters from './useFilters';
import cx from 'classnames';
import humanize from 'underscore.string/humanize';
import history from 'lib/history';
import truthy from 'lib/truthy';
import { ltButtonBarBg } from "./colors";

function useLoad() {
  return history.getQuery().load || {};
}

function useImagesInListView() {
  const load = useLoad();
  const s = useContextState();
  return truthy(load.showImagesInListView) || s.showImagesInListView;
}

function LTListViewHeaderOrderButton() {
  const cmd = useCommands();
  const { listViewOrder } = useContextState();

  const onClick = () => {
    const newOrder = listViewOrder === 'name' ? 'updated_at' : 'name';
    const newDir = newOrder === 'updated_at' ? 'desc' : 'asc';
    cmd.set(['listViewOrder'], newOrder);
    cmd.set(['listViewOrderDir'], newDir);
  };

  const label = listViewOrder === 'name' ? 'Name' : 'Last Updated';

  return (
    <button
      className="border-bottom ml-1 p-0 user-select-none text-muted"
      onClick={onClick}
      data-testid="LTListViewHeaderOrderButton"
    >
      {label} <i className="fa fa-sort" />
    </button>
  );
}

function LTListViewEmpty() {
  let msg = isPath('/map') ? (
    <Fragment>
      Tip: Click on the map, search for an address, then add a single record ––
      or{' '}
      <a className="text-link" href="/tables?upload=true">
        bulk import
      </a>{' '}
      your records.
    </Fragment>
  ) : (
    <Fragment>Zoom out or re-center your map view to locate records.</Fragment>
  );

  return (
    <div
      className="pl-5 pr-4 pt-5 d-flex align-items-center text-justified"
      data-testid="LTListViewEmpty"
      css={css(`
        color: #919191;
        font-size: 2rem;
      `)}
    >
      <div>{msg}</div>
    </div>
  );
}

function resizedPhoto(value, config = 'w:335') {
  const key = ENV.FILEPICKER_API_KEY;
  // Resize the logo image to 100x50
  const imgSrc = `https://cdn.filestackcontent.com/${key}/resize=${config}/rotate=deg:exif/${value}`;
  return imgSrc;
}

export function LTListViewItem(props: any): Element<string> {
  const cmd = useCommands();
  const showImages = useImagesInListView();
  const hasPhoto = props.photo;

  function getItemType(v) {
    if (v.type === 'Tenant') return 'Businesses & Attractions';
    return humanize(v.type);
  }

  return (
    <div
      data-testid="LTListViewItem"
      css={css(`
        background: white;
        ${
          props.notPartOfList
            ? ''
            : `cursor: pointer;
          &:hover {
            background: #f2f2f2;
          }`
        }
      `)}
      onClick={() => {
        cmd.showItemView(props);
        const [lat, lon] = props.latlon;
        cmd.panTo(lat, lon);
      }}
    >
      <div
        css={css(`
        width: calc(100% - 10px);
        padding: 2rem 0.5rem 2rem 3rem;
      `)}
      >
        <div
          css={css(`
        display: flex;
        justify-content: space-between;
      `)}
        >
          <div
            css={css(`

        `)}
          >
            <div data-testid="LTListViewItemHeader" className="mb-2">
              <div title={props.name} className="h4 m-0 font-weight-bold">
                {truncate(props.name, 33)}
              </div>
            </div>
            <div data-testid="LTListViewItemBody">
              <div
                className="text-uppercase"
                css={css('font-size: 1rem; color: #636363;')}
              >
                {getItemType(props)}
              </div>
              <div css={css('font-size: 1.2rem;')}>
                <i
                  className="fa fa-circle"
                  style={{ color: props.fillColor }}
                />{' '}
                {props.category || props.status}
              </div>
            </div>
          </div>
          <div
            className={cx('align-items-center pr-5', {
              'd-flex': !props.notPartOfList,
              'd-none': props.notPartOfList,
            })}
          >
            <i className="fa fa-angle-right" css={css('font-size: 1.8rem;')} />
          </div>
        </div>
        {showImages && hasPhoto && (
          <div
            data-testid="LTListViewItemBodyImage"
            css={css('margin-top: 2rem; max-height: 188px; overflow: hidden;')}
          >
            <img src={resizedPhoto(props.photo)} />
          </div>
        )}
      </div>
    </div>
  );
}

export function LTListViewSkeletonListItem(): Element<string> {
  return (
    <div
      data-testid="LTListViewSkeletonListItem"
      css={css('padding: 1rem 3rem 3rem 3rem;')}
    >
      <div
        css={css('background: #efefef; margin-bottom: 0.5rem;height: 2rem;')}
      />
      <div>
        <span
          css={css(`
            background: #efefef;
            color: #efefef;
            width: 50px;
            height: 1rem;
            display: inline-block;
          `)}
        />
      </div>
      <div>
        <i
          className="fa fa-circle"
          css={css(`
            color: #efefef;
          `)}
        />{' '}
        <span
          css={css(
            'background: #efefef; width: 20px; height: 1.2rem; display: inline-block;'
          )}
        />
      </div>
    </div>
  );
}

function LTListViewSkeletonList() {
  return (
    <div data-testid="LTListViewSkeletonList">
      {I.Range(0, 4).map((i) => (
        <LTListViewSkeletonListItem key={i} />
      ))}
    </div>
  );
}

function LTListViewNavSummary({ totalCount, floorCount, ceilCount }) {
  if (totalCount <= 0) return <div />;
  return (
    <div data-testid="LTListViewNavSummary" className="text-right">
      {floorCount + 1} to {ceilCount} of {totalCount} results
    </div>
  );
}

function useIOSHack() {
  const containerRef = createRef();
  const summaryRef = createRef();
  const pagerRef = createRef();
  const mainRef = createRef();
  const { locHeaderToolsOpen } = useContextState();

  useEffect(() => {
    const iOS = /(iPad|iPhone|iPod)/g.test(navigator.userAgent);
    if (!iOS) return;
    const summaryEl = summaryRef.current;
    const mainEl = mainRef.current;
    const pagerEl = pagerRef.current;
    const containerEl = containerRef.current;
    if (!summaryEl || !mainEl || !pagerEl || !containerEl) return;
    const mainHeight =
      containerEl.offsetHeight - summaryEl.offsetHeight - pagerEl.offsetHeight;
    $(mainEl).height(mainHeight);
  }, [summaryRef, mainRef, pagerRef, containerRef, locHeaderToolsOpen]);

  return {
    containerRef,
    summaryRef,
    pagerRef,
    mainRef,
  };
}

export default function LTListView(): null | Element<string> {
  const {
    listViewOrder: order,
    listViewOrderDir: orderDir,
    focusedLatlon,
  } = useContextState();
  const ROW_COUNT = 10;
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const [floorCount, setFloorCount] = useState(0);
  const [first, setFirst] = useState(ROW_COUNT);
  const [last, setLast] = useState(null);
  const filters = useFilters().toJS();
  const variables = {
    filters,
    first,
    last,
    after,
    before,
    order: focusedLatlon ? null : order,
    orderDir: focusedLatlon ? null : orderDir,
  };
  const opts = { variables };
  const res = useCachedQuery(GetProjectsPaginated, opts);
  const { data, loading, refetch } = res;
  const payload = I.getIn(data, ['projectList'], {});

  // Scroll top when the pagination happens
  useEffect(() => {
    scrollContainerTop();
    refetch(variables);
  }, [variables]);

  // Update fetched number of records when focusedLatlon changes
  useEffect(() => {
    setFirst((v) => (v ? ROW_COUNT : v));
    setLast((v) => (v ? ROW_COUNT : v));
  }, [focusedLatlon]);
  const { containerRef, summaryRef, pagerRef, mainRef } = useIOSHack();

  if (!payload) return null;

  const { pageInfo = {}, nodes = [], totalCount } = payload;
  const projects = nodes;
  const { startCursor, endCursor } = pageInfo;
  const ceilCount = Math.min(projects.length, ROW_COUNT) + floorCount;
  const enablePrevBtn = floorCount > 1;
  const enableNextBtn = ceilCount < totalCount;

  function prevPage() {
    if (!enablePrevBtn) return;
    setBefore(startCursor);
    setAfter(null);
    setFirst(null);
    setLast(ROW_COUNT);
    setFloorCount((v) => v - ROW_COUNT);
  }

  function nextPage() {
    if (!enableNextBtn) return;
    setBefore(null);
    setAfter(endCursor);
    setFirst(ROW_COUNT);
    setLast(null);
    setFloorCount((v) => v + ROW_COUNT);
  }

  const numSummaryText = `${totalCount || 0} records ${
    focusedLatlon ? 'near this location' : 'shown'
  }`;

  const hasData = totalCount > 0;

  return (
    <div data-testid="LTListView" css={css('position: relative;')}>
      <div
        css={css(`
        overflow: hidden;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        position: absolute;
      `)}
        ref={containerRef}
      >
        <div
          data-testid="LTListViewHeader"
          css={css(`
          border-bottom: solid 1px #efefef;
          display: flex;
          justify-content: space-between;
          color: #626262;
          padding: 1rem 3rem 1rem 3rem;
          ${loading ? 'visibility: hidden' : ''}

          height: 43px;
        `)}
          ref={summaryRef}
        >
          <div>{numSummaryText}</div>
          {hasData && !focusedLatlon && (
            <div>
              Sorted by <LTListViewHeaderOrderButton />
            </div>
          )}
        </div>
        <div
          css={css(`
            -webkit-overflow-scrolling: touch;
            overscroll-behavior: contain;
            overflow-y: scroll;

            height: calc(100% - 77px);
        `)}
          ref={mainRef}
        >
          {loading ? (
            <LTListViewSkeletonList />
          ) : hasData ? (
            projects.map((v, i) => <LTListViewItem {...v} key={i} />)
          ) : (
            <LTListViewEmpty />
          )}
        </div>
        <div
          data-testid="LTListViewNav"
          css={css(`
            * { color: #636363; };
            border-top: solid 1px #efefef;
            display: flex;
            justify-content: space-between;
            padding: 0.5rem 3rem 0.5rem 3rem;

            height: 34px;
          `)}
          ref={pagerRef}
        >
          <LTListViewNavSummary
            totalCount={totalCount}
            floorCount={floorCount}
            ceilCount={ceilCount}
          />
          <div
            data-testid="LTListViewNavBtns"
            css={css(`
              button[disabled] {
                cursor: no-drop;
              }
            `)}
          >
            <button
              data-testid="LTListViewNavBtnsPrevBtn"
              onClick={prevPage}
              disabled={!enablePrevBtn}
            >
              Prev
            </button>
            <button
              data-testid="LTListViewNavBtnsNextBtn"
              onClick={nextPage}
              disabled={!enableNextBtn}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
